import Link from "next/link";
import * as React from "react";

import { cn } from "@/lib/cn";

type LinkButtonVariant = "primary" | "outline" | "ghost";

type BaseProps = {
  variant?: LinkButtonVariant;
  /** If true, renders aria-disabled and prevents clicks via onClick handler */
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
} & { href: string; target?: string; rel?: string; prefetch?: boolean }; // href required for Next.js Link

export function LinkButton({
  variant = "primary",
  disabled = false,
  className,
  children,
  href,
  target,
  rel,
  prefetch,
  ...rest
}: BaseProps & Omit<React.ComponentPropsWithoutRef<typeof Link>, "href" | "className">) {
  const base =
    // layout + type
    "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium " +
    // transitions + motion
    "transition motion-reduce:transition-none will-change-transform active:translate-y-[0.5px] " +
    // focus ring with offset
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] " +
    "focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--bg))] " +
    // make anchors look like buttons
    "no-underline";

  const variants: Record<LinkButtonVariant, string> = {
    primary: "bg-[hsl(var(--brand))] text-[hsl(var(--brand-fg))] hover:opacity-90",
    outline: "border border-border bg-card hover:bg-[hsl(var(--muted))] text-fg",
    ghost: "bg-transparent hover:bg-[hsl(var(--muted))] text-fg",
  };

  const disabledCls = disabled ? "opacity-60 pointer-events-none select-none" : "";

  // If target is _blank, ensure rel has noreferrer noopener by default
  const computedRel =
    target === "_blank" ? [rel, "noopener", "noreferrer"].filter(Boolean).join(" ") : rel;

  return (
    <Link
      href={disabled ? "#" : href}
      target={target}
      rel={computedRel}
      aria-disabled={disabled || undefined}
      className={cn(base, variants[variant], disabledCls, className)}
      prefetch={prefetch}
      {...rest}
    >
      {children}
    </Link>
  );
}
